import { ChevronDown, LogOut, User } from "lucide-react";
import { useState } from "react";
import { logout } from "wasp/client/auth";
import { Link as WaspRouterLink } from "wasp/client/router";
import { Link as RouterLink } from "react-router-dom";
import { type User as UserEntity } from "wasp/entities";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "../components/ui/dropdown-menu";
import { userMenuItems } from "./constants";

export function UserDropdown({ 
  user, 
  actions 
}: { 
  user: Partial<UserEntity>;
  actions?: React.ReactNode;
}) {
  const [open, setOpen] = useState(false);

  return (
    <DropdownMenu open={open} onOpenChange={setOpen}>
      <DropdownMenuTrigger asChild>
        <button className="flex items-center gap-2 px-3 py-2 rounded-lg duration-200 ease-in-out text-foreground hover:bg-muted/50 transition-all">
          <div className="flex items-center justify-center size-8 rounded-full bg-primary/10 text-primary">
            <User className="size-4" />
          </div>
          <span className="hidden lg:block text-sm font-medium text-foreground">
            {user.username}
          </span>
          <ChevronDown
            className={`size-4 text-muted-foreground transition-transform duration-200 ${open ? "rotate-180" : ""
              }`}
          />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="end"
        sideOffset={8}
        className="w-56 p-2 rounded-xl shadow-lg border border-border/50 bg-popover/95 backdrop-blur-sm"
      >
        {/* User info header */}
        <div className="px-3 py-2.5 mb-1">
          <p className="text-sm font-semibold text-foreground">
            {user.username}
          </p>
          {user.email && (
            <p className="text-xs text-muted-foreground truncate">
              {user.email}
            </p>
          )}
        </div>
        
        {actions && (
          <>
            <DropdownMenuSeparator className="my-1.5" />
            <div className="px-1 py-1">
              {actions}
            </div>
          </>
        )}
        <DropdownMenuSeparator className="my-1.5" />

        {userMenuItems.map((item) => {
          if (item.isAuthRequired && !user) return null;
          if (item.isAdminOnly && (!user || !user.isAdmin)) return null;

          return (
            <DropdownMenuItem
              key={item.name}
              asChild
              className="px-3 py-2.5 rounded-lg cursor-pointer"
            >
              <RouterLink
                to={item.to}
                onClick={() => {
                  setOpen(false);
                }}
                className="flex items-center gap-3 w-full text-sm"
              >
                <item.icon className="size-4 text-muted-foreground" />
                <span>{item.name}</span>
              </RouterLink>
            </DropdownMenuItem>
          );
        })}

        <DropdownMenuSeparator className="my-1.5" />

        <DropdownMenuItem
          onClick={() => logout()}
          className="px-3 py-2.5 rounded-lg cursor-pointer text-destructive hover:text-destructive hover:bg-destructive/10 focus:text-destructive focus:bg-destructive/10"
        >
          <LogOut className="size-4 mr-3" />
          <span className="text-sm">Log Out</span>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
